import DiffMatchPatch from 'diff-match-patch';
import * as Y from 'yjs';
import type { DiffRange } from '@/lib/diff/types';

// Re-export so consumers can still import DiffRange from this engine path.
export type { DiffRange } from '@/lib/diff/types';

/**
 * Computes text-level diff between a current Y.Doc and a snapshot Y.Doc.
 *
 * Uses diff-match-patch on the extracted plain text from both docs.
 * Returns ranges relative to the current doc (added) and snapshot doc (deleted).
 */
export function computeDocumentDiff(currentDoc: Y.Doc, snapshotDoc: Y.Doc): DiffRange[] {
  const currentText = extractText(currentDoc);
  const snapshotText = extractText(snapshotDoc);

  if (currentText === snapshotText) return [];

  const dmp = new DiffMatchPatch();
  const diffs = dmp.diff_main(snapshotText, currentText);
  dmp.diff_cleanupSemantic(diffs);

  const ranges: DiffRange[] = [];
  let currentOffset = 0;
  let snapshotOffset = 0;

  for (const [op, text] of diffs) {
    const len = text.length;
    if (op === DiffMatchPatch.DIFF_EQUAL) {
      currentOffset += len;
      snapshotOffset += len;
    } else if (op === DiffMatchPatch.DIFF_INSERT) {
      // Text added in current (not in snapshot)
      ranges.push({ type: 'added', offset: currentOffset, length: len });
      currentOffset += len;
    } else if (op === DiffMatchPatch.DIFF_DELETE) {
      // Text deleted from snapshot (not in current)
      ranges.push({ type: 'deleted', offset: snapshotOffset, length: len });
      snapshotOffset += len;
    }
  }

  return ranges;
}

/** Extract plain text from a Y.Doc's Tiptap/y-prosemirror XmlFragment. */
function extractText(doc: Y.Doc): string {
  try {
    const fragment = doc.getXmlFragment('prosemirror');
    return xmlNodeToText(fragment);
  } catch {
    return '';
  }
}

function xmlNodeToText(node: Y.XmlFragment | Y.XmlElement | Y.XmlText): string {
  if (node instanceof Y.XmlText) {
    // toDelta() returns [{insert: string, attributes?: ...}] — gives plain text
    // without XML markup that toString() includes (e.g. <bold>word</bold>)
    return node.toDelta().map((op: { insert?: unknown }) =>
      typeof op.insert === 'string' ? op.insert : ''
    ).join('');
  }
  // Y.XmlFragment and Y.XmlElement both have .length and .get(i)
  let text = '';
  for (let i = 0; i < node.length; i++) {
    const child = node.get(i);
    if (child instanceof Y.XmlFragment || child instanceof Y.XmlElement || child instanceof Y.XmlText) {
      text += xmlNodeToText(child);
    }
  }
  return text;
}
